import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);
import { PinnedSectionDefaults } from "../base";
import type { AnimationActions, ScrollTriggerProps } from "../types";
import {
  computeTextShifts,
  getCopyInProps,
  getHeadlineOutProps,
  getLeftInProps,
  getLeftOutProps,
  getRightInProps,
  inProps,
  outProps,
  SELECTORS,
} from "./helpers";
import { FADE_HORIZONTAL_STEP_DISTANCE_FACTOR, getStepRange } from "../helpers";

const animation: AnimationActions = {
  onEnter: ({ section, subElements }) => {
    if (!subElements) return;
    const { bg, headline, copy, left } = subElements;
    if (!bg || !headline || !copy || !left) return;

    gsap.to(section, {
      ...inProps,
      overwrite: "auto",
    });
    gsap.to(left, {
      ...getLeftInProps(section),
      overwrite: "auto",
    });
    gsap.to(headline, {
      ...getRightInProps(),
      overwrite: "auto",
    });
    gsap.to(copy, {
      ...getCopyInProps(),
      overwrite: "auto",
    });
    gsap.to(bg, {
      ...inProps,
      overwrite: "auto",
    });
    gsap.to(headline, {
      x: 0,
      duration: 0.4,
      ease: "power2.out",
      overwrite: "auto",
    });
    gsap.to(copy, {
      x: 0,
      duration: 0.4,
      ease: "power2.out",
      overwrite: "auto",
    });
  },
  onEnterBack: ({ section, subElements }) => {
    if (!subElements) return;
    const { bg, headline, copy, left } = subElements;
    if (!bg || !headline || !copy || !left) return;

    gsap.to(section, {
      ...inProps,
      overwrite: "auto",
    });
    gsap.to(left, {
      ...getLeftInProps(section),
      overwrite: "auto",
    });
    gsap.to(headline, {
      ...getRightInProps(),
      overwrite: "auto",
    });
    gsap.to(copy, {
      ...getCopyInProps(),
      overwrite: "auto",
    });
    gsap.to(bg, {
      ...inProps,
      overwrite: "auto",
    });
    gsap.to(headline, {
      x: 0,
      duration: 0.4,
      ease: "power2.out",
      overwrite: "auto",
    });
    gsap.to(copy, {
      x: 0,
      duration: 0.4,
      ease: "power2.out",
      overwrite: "auto",
    });
  },
  onLeave: ({ section, subElements }) => {
    if (!subElements) return;
    const { left, bg, headline, copy } = subElements;
    if (!bg || !headline || !copy || !left) return;
    const shifts = computeTextShifts(section);

    gsap.to(left, {
      ...getLeftOutProps(section),
      overwrite: "auto",
    });
    gsap.to(section, {
      opacity: 0,
      duration: 0.4,
      overwrite: "auto",
    });
    gsap.to(copy, {
      left: "5%",
      opacity: 0,
      duration: 0.4,
      overwrite: "auto",
    });
    gsap.to(headline, {
      ...getHeadlineOutProps(),
      x: shifts.headline,
      overwrite: "auto",
    });
    gsap.to(bg, {
      ...outProps,
      overwrite: "auto",
    });
    gsap.to(copy, {
      x: shifts.copy,
      duration: 0.4,
      ease: "power2.inOut",
      overwrite: "auto",
    });
  },
  onLeaveBack: ({ section, subElements }) => {
    if (!subElements) return;
    const { left, bg, headline, copy } = subElements;
    if (!bg || !headline || !copy || !left) return;
    const shifts = computeTextShifts(section);

    gsap.to(section, {
      opacity: 0,
      duration: 0.4,
      overwrite: "auto",
    });
    gsap.to(left, {
      ...getLeftOutProps(section),
      overwrite: "auto",
    });
    gsap.to(copy, {
      left: "5%",
      opacity: 0,
      duration: 0.4,
      overwrite: "auto",
    });
    gsap.to(headline, {
      ...getHeadlineOutProps(),
      x: shifts.headline,
      overwrite: "auto",
    });
    gsap.to(bg, {
      ...outProps,
      overwrite: "auto",
    });
    gsap.to(copy, {
      x: shifts.copy,
      duration: 0.4,
      ease: "power2.inOut",
      overwrite: "auto",
    });
  },
};

const lastAnimation: Required<
  Pick<AnimationActions, "onLeave" | "onEnterBack">
> = {
  onLeave: ({ section }) => {
    gsap.to(section, {
      top: "-50vh",
      duration: 0.4,
      overwrite: "auto",
    });
    gsap.to(section, {
      opacity: 0,
      duration: 0.4,
      overwrite: "auto",
    });
  },
  onEnterBack: ({ section }) => {
    gsap.to(section, {
      top: "0",
      ...inProps,
      overwrite: "auto",
    });
  },
};
const firstAnimation: Required<
  Pick<AnimationActions, "onEnter" | "onLeaveBack">
> = {
  onEnter: ({ section }) => {
    gsap.to(section, {
      top: "0",
      duration: 0.4,
      overwrite: "auto",
    });
  },
  onLeaveBack: ({ section }) => {
    gsap.to(section, {
      top: "100vh",
      duration: 0.4,
      overwrite: "auto",
    });
  },
};

type ResponsiveState = "in" | "out";

const syncResponsivePositions = ({
  section,
  media,
  headline,
  copy,
  bg,
  state,
}: {
  section: HTMLElement;
  media: HTMLElement;
  headline: HTMLElement;
  copy: HTMLElement;
  bg: HTMLElement;
  state: ResponsiveState;
}) => {
  gsap.killTweensOf([media, headline, copy, bg]);
  gsap.set(headline, { clearProps: "left,x,transform" });
  gsap.set(headline.querySelectorAll(".mediatext__line"), {
    clearProps: "right,left,x,transform",
  });

  if (state === "in") {
    gsap.set(media, getLeftInProps(section));
    gsap.set(headline, {
      ...getRightInProps(),
      x: 0,
    });
    gsap.set(copy, {
      ...getCopyInProps(),
      x: 0,
    });
    gsap.set(bg, inProps);
    return;
  }

  const shifts = computeTextShifts(section);
  gsap.set(media, getLeftOutProps(section));
  gsap.set(headline, {
    ...getHeadlineOutProps(),
    x: shifts.headline,
  });
  gsap.set(copy, {
    left: "5%",
    opacity: 0,
    x: shifts.copy,
  });
  gsap.set(bg, outProps);
};

const scheduleResponsiveSync = (sync: () => void) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(sync);
  });
};

export const FadeHorizontalScrollTrigger = ({
  timeline,
  section,
  step,
  triggerElement,
}: ScrollTriggerProps) => {
  const media = section.querySelector(SELECTORS.media!) as HTMLElement;
  const background = section.querySelector(SELECTORS.bg!) as HTMLElement;
  const headline = section.querySelector(
    SELECTORS.headline!
  ) as HTMLElement | null;
  const copy = section.querySelector(SELECTORS.copy!) as HTMLElement | null;
  if (!media || !headline || !background || !copy)
    throw new Error("Media or content not given");
  const initialShifts = computeTextShifts(section);
  if (headline) {
    gsap.set(headline, { x: initialShifts.headline });
  }
  if (copy) {
    gsap.set(copy, { x: initialShifts.copy });
  }
  const subElements = {
    left: media,
    headline,
    bg: background,
    copy,
  };
  const horizontals = triggerElement.querySelectorAll(SELECTORS.horizontals!);
  const slide = section.querySelector(SELECTORS.slide!) as HTMLElement;
  const isFirst = slide === horizontals[0];
  const isLast = slide === horizontals[horizontals.length - 1];
  let responsiveState: ResponsiveState = isFirst ? "in" : "out";
  if (!horizontals) throw new Error("No horizontals found");
  if (!isFirst) {
    gsap.set(section, { top: 0, opacity: 0 });
    gsap.set(media, getLeftOutProps(section));
    gsap.set(background, outProps);
  }
  if (isFirst) {
    gsap.set(section, { top: "100vh", opacity: 1 });
    gsap.set(media, getLeftInProps(section));
    gsap.set(headline, getRightInProps());
    gsap.set(headline, { x: 0 });
    gsap.set(copy, getCopyInProps());
    gsap.set(copy, { x: 0 });
  }
  const getRange = () =>
    getStepRange(step, 0, FADE_HORIZONTAL_STEP_DISTANCE_FACTOR);
  let viewportWidth = window.innerWidth;
  let viewportHeight = window.innerHeight;
  const st = ScrollTrigger.create({
    ...PinnedSectionDefaults,
    pin: false,
    scrub: false,
    invalidateOnRefresh: true,
    trigger: triggerElement,
    start: () => getRange().start,
    end: () => getRange().end,
    onRefresh: () => {
      const nextViewportWidth = window.innerWidth;
      const nextViewportHeight = window.innerHeight;

      if (
        nextViewportWidth === viewportWidth &&
        nextViewportHeight === viewportHeight
      ) {
        return;
      }

      viewportWidth = nextViewportWidth;
      viewportHeight = nextViewportHeight;

      scheduleResponsiveSync(() => {
        syncResponsivePositions({
          section,
          media,
          headline,
          copy,
          bg: background,
          state: responsiveState,
        });
      });
    },
    onLeave: (self) => {
      if (isLast) {
        responsiveState = "in";
        lastAnimation.onLeave({ self, section, timeline, step });
      } else {
        responsiveState = "out";
        animation.onLeave({ self, section, timeline, step, subElements });
      }
    },
    onLeaveBack: (self) => {
      if (isFirst) {
        responsiveState = "in";
        firstAnimation.onLeaveBack({ self, section, timeline, step });
      } else {
        responsiveState = "out";
        animation.onLeaveBack({
          self,
          section,
          timeline,
          step,
          subElements,
        });
      }
    },
    onEnter: (self) => {
      responsiveState = "in";
      if (isFirst) {
        firstAnimation.onEnter({ self, section, timeline, step });
      } else {
        animation.onEnter({
          self,
          section,
          timeline,
          step,
          subElements,
        });
      }
    },
    onEnterBack: (self) => {
      responsiveState = "in";
      if (isLast) {
        lastAnimation.onEnterBack({ self, section, timeline, step });
      } else {
        animation.onEnterBack({
          self,
          section,
          timeline,
          step,
          subElements,
        });
      }
    },
  });
  return st;
};
